<script setup lang="ts">
import { IconAlertTriangle, IconInfoCircle } from '@tabler/icons-vue';
import { computed, ref, watch } from 'vue';

import DateRangePicker from '~/components/shared/DateRangePicker.vue';
import GenericModal from '~/components/shared/GenericModal.vue';
import { RefundOrigin } from '~/services/receipt-model-api/receipt-model-api.types';

import { REFUND_ORIGIN_LABEL, REFUND_ORIGIN_OPTIONS } from '../constants';
import {
  getTotalDaysSinceFormalization,
  isMoreThanSevenBusinessDays,
} from '../utils/businessDays';

interface Props {
  formalizationDate?: string;
  isFormalized?: boolean;
  isLoading?: boolean;
  modelValue: boolean;
  requiresForce?: boolean;
  showSevenDaysWarning?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  formalizationDate: '',
  isFormalized: false,
  isLoading: false,
  requiresForce: false,
  showSevenDaysWarning: false,
});

const emit = defineEmits<{
  'confirm': [data: { cancelled_date?: string; force?: boolean; origin?: RefundOrigin; reason: string }];
  'update:modelValue': [value: boolean];
}>();

const baseCancelationOptions = [
  {
    label: 'Suspeita de fraude',
    value: 'risk_suspected',
  },
  {
    label: 'Fraude comprovada',
    value: 'risk_fraud',
  },
  {
    label: 'Outro',
    value: 'risk_other',
  },
];

const formalizedOnlyOptions = [
  {
    label: 'Cancelamento solicitado pelo cliente',
    value: 'client_requested',
  },
];

const cancelationOptions = computed(() => {
  const options = [...baseCancelationOptions];

  if (props.isFormalized) {
    options.splice(-1, 0, ...formalizedOnlyOptions);
  }

  return options;
});

const selectedCancelationOption = ref('risk_suspected');
const customReason = ref('');
const forceCancel = ref(false);
const cancelledDate = ref('');
const refundOrigin = ref<'' | RefundOrigin>('');

const isDateInFuture = (dateString: string) => {
  if (!dateString) return false;
  const selectedDate = new Date(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  selectedDate.setHours(0, 0, 0, 0);
  return selectedDate > today;
};

const isVisible = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value),
});

const totalDaysSinceFormalization = computed(() => getTotalDaysSinceFormalization(props.formalizationDate || ''));

const isMoreThanSevenDays = computed(() => isMoreThanSevenBusinessDays(props.formalizationDate || ''));

const showWarning = computed(() => props.isFormalized && (props.showSevenDaysWarning || isMoreThanSevenDays.value));

const characterCount = computed(() => (
  selectedCancelationOption.value === 'risk_other' ? customReason.value?.length || 0 : 0
));

const dateErrorMessage = computed(() => {
  if (!props.isFormalized || selectedCancelationOption.value !== 'client_requested') return '';
  if (!cancelledDate.value.trim()) return 'Data de cancelamento é obrigatória';
  if (isDateInFuture(cancelledDate.value)) return 'Não é permitido informar uma data futura';
  return '';
});

const isFormValid = computed(() => {
  if (!selectedCancelationOption.value) return false;

  if (selectedCancelationOption.value === 'risk_other') {
    const customReasonTrimmed = customReason.value.trim();
    if (customReasonTrimmed.length === 0) return false;

    if (props.isFormalized && (customReasonTrimmed.length < 10 || customReasonTrimmed.length > 500)) {
      return false;
    }
  }

  if (props.isFormalized && selectedCancelationOption.value === 'client_requested') {
    if (!cancelledDate.value.trim()) return false;

    if (isDateInFuture(cancelledDate.value)) return false;
  }

  if (props.isFormalized && props.requiresForce && !forceCancel.value) {
    return false;
  }

  if (props.isFormalized && !refundOrigin.value) return false;

  return true;
});

const handleSave = () => {
  if (!isFormValid.value) return;

  let reason = '';

  const reasonMapping = {
    client_requested: 'Cancelamento solicitado pelo cliente',
    risk_fraud: 'Fraude comprovada',
    risk_other: customReason.value.trim(),
    risk_suspected: 'Suspeita de Fraude',
  };

  reason = reasonMapping[selectedCancelationOption.value as keyof typeof reasonMapping]
    || selectedCancelationOption.value;

  const shouldIncludeCancelledDate = props.isFormalized
    && selectedCancelationOption.value === 'client_requested';

  emit('confirm', {
    cancelled_date: shouldIncludeCancelledDate ? cancelledDate.value : undefined,
    force: props.requiresForce ? forceCancel.value : undefined,
    origin: props.isFormalized && refundOrigin.value ? refundOrigin.value : undefined,
    reason,
  });
};

const handleCancel = () => {
  isVisible.value = false;
};

watch(() => props.modelValue, (isOpen) => {
  if (!isOpen) {
    selectedCancelationOption.value = 'risk_suspected';
    customReason.value = '';
    forceCancel.value = false;
    cancelledDate.value = '';
    refundOrigin.value = '';
  }
});

watch(() => props.requiresForce, (requires) => {
  if (!requires) {
    forceCancel.value = false;
  }
});
</script>

<template>
  <GenericModal
    v-model="isVisible"
    :title="isFormalized ? 'Cancelar Projeto Formalizado' : 'Cancelar Projeto'"
    :loading="isLoading"
    :primary-button-disabled="!isFormValid"
    primary-button-text="Confirmar Cancelamento"
    primary-button-class="btn-danger"
    secondary-button-text="Cancelar"
    @primary-action="handleSave"
    @secondary-action="handleCancel"
  >
    <div v-if="showWarning" class="alert alert-warning mb-3" role="alert">
      <div class="d-flex align-items-center">
        <IconAlertTriangle class="alert-icon me-2" />
        <div>
          <strong>Atenção:</strong> Você está cancelando um financiamento com
          <strong>{{ totalDaysSinceFormalization }} dias</strong> de formalização.
        </div>
      </div>
    </div>

    <div v-if="requiresForce" class="alert alert-info mb-3" role="alert">
      <div class="d-flex align-items-center">
        <IconInfoCircle class="alert-icon me-2" />
        <div>
          Este cancelamento requer confirmação adicional devido ao prazo de formalização.
        </div>
      </div>
    </div>

    <div class="text-center mb-4">
      <IconAlertTriangle class="mb-2 text-danger icon-lg" :size="48" />
      <h3>Cancelar Projeto</h3>
      <div class="text-secondary">
        Selecione o motivo do cancelamento abaixo:
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <div class="form-group mb-3">
          <label class="form-label required">Motivo do cancelamento</label>
          <div class="form-selectgroup form-selectgroup-boxes d-flex flex-column">
            <label
              v-for="option in cancelationOptions"
              :key="option.value"
              class="form-selectgroup-item flex-fill"
            >
              <input
                type="radio"
                :value="option.value"
                v-model="selectedCancelationOption"
                class="form-selectgroup-input"
                :disabled="isLoading"
              />
              <div class="form-selectgroup-label d-flex align-items-center p-3">
                <div class="me-3">
                  <span class="form-selectgroup-check" />
                </div>
                <div class="form-selectgroup-label-content d-flex align-items-center">
                  <div>
                    <div class="font-weight-medium">{{ option.label }}</div>
                  </div>
                </div>
              </div>
            </label>
          </div>
        </div>

        <div v-if="isFormalized" class="form-group mb-3">
          <label class="form-label required">{{ REFUND_ORIGIN_LABEL }}</label>
          <div class="form-selectgroup form-selectgroup-boxes d-flex flex-column">
            <label
              v-for="option in REFUND_ORIGIN_OPTIONS"
              :key="option.value"
              class="form-selectgroup-item flex-fill"
            >
              <input
                type="radio"
                :value="option.value"
                v-model="refundOrigin"
                class="form-selectgroup-input"
                :disabled="isLoading"
              />
              <div class="form-selectgroup-label d-flex align-items-center p-3">
                <div class="me-3">
                  <span class="form-selectgroup-check" />
                </div>
                <div class="form-selectgroup-label-content">
                  <div class="font-weight-medium">{{ option.description }}</div>
                </div>
              </div>
            </label>
          </div>
        </div>

        <div v-if="selectedCancelationOption === 'risk_other'" class="form-group mb-3">
          <label for="custom-reason" class="form-label required">Descreva o motivo</label>
          <textarea
            id="custom-reason"
            v-model="customReason"
            class="form-control"
            rows="3"
            placeholder="Digite o motivo específico do cancelamento..."
            :maxlength="isFormalized ? 500 : undefined"
            :disabled="isLoading"
          />
          <div v-if="isFormalized || selectedCancelationOption === 'risk_other'" class="form-hint">
            {{ characterCount }}{{ isFormalized ? '/500' : '' }} caracteres
            <span v-if="isFormalized && customReason.trim().length < 10" class="text-danger ms-2">
              (mínimo 10 caracteres)
            </span>
          </div>
        </div>

        <div v-if="isFormalized && selectedCancelationOption === 'client_requested'" class="form-group mb-3">
          <label for="cancelled-date" class="form-label required">Data do cancelamento</label>
          <DateRangePicker
            id="cancelled-date"
            v-model="cancelledDate"
            mode="single"
            placeholder="Selecione a data do cancelamento"
            :disabled="isLoading"
            date-format="d/m/Y"
            style="min-height: auto;"
          />
          <div v-if="dateErrorMessage" class="text-danger mt-1 small">
            {{ dateErrorMessage }}
          </div>
        </div>

        <div v-if="isFormalized && requiresForce" class="form-group mb-3">
          <div class="form-check">
            <input
              id="force-cancel"
              v-model="forceCancel"
              class="form-check-input"
              type="checkbox"
              :disabled="isLoading"
            >
            <label class="form-check-label" for="force-cancel">
              <strong>Confirmo que desejo forçar o cancelamento</strong> mesmo com mais de 7 dias de formalização
            </label>
          </div>
        </div>
      </div>
    </div>
  </GenericModal>
</template>

<style scoped lang="scss">
.form-control {
  resize: vertical;
  min-height: 100px;
}

.alert-icon {
  flex-shrink: 0;
}

.icon-lg {
  width: 3rem;
  height: 3rem;
}
</style>
